import { useMemo } from 'react';
import type { TrackerEmail, EmailAttachment } from '../types/tracker.types';

/**
 * Helper to generate unique ID for attachments
 * Must match the logic in ProcessEmailModal and AttachmentSelector
 */
const getAttachmentUniqueId = (att: { id?: string; name: string }, idx: number): string => {
  return att.id || `attachment-${idx}-${att.name}`;
};

/**
 * Extended attachment with email context
 */
export interface AttachmentWithContext extends EmailAttachment {
  emailId: string;
  emailSubject: string | null;
  emailDate: string;
  senderName: string | null;
  senderAddress: string | null;
  attachmentIndex: number;
  emailType: 'received' | 'sent';
}

/**
 * Hook to extract and aggregate all attachments from emails for an affaire
 * @param emails - Array of tracker emails
 * @returns Array of attachments with email context, sorted by date (newest first)
 */
export const useAllAttachments = (emails: TrackerEmail[] | undefined) => {
  return useMemo(() => {
    if (!emails || emails.length === 0) {
      return [];
    }

    const allAttachments: AttachmentWithContext[] = [];

    emails.forEach((email) => {
      // Only process emails that have attachments
      if (email.has_attachments && email.attachments && email.attachments.length > 0) {
        // Build set of excluded attachment IDs for this email
        const excludedIds = new Set(email.excluded_attachments || []);

        // Filter out inline attachments but keep original indices for download URLs
        email.attachments.forEach((attachment, originalIndex) => {
          // Skip inline attachments (signature images, etc.)
          if (attachment.isInline || attachment.is_inline) return;

          // Generate unique ID using same logic as ProcessEmailModal/AttachmentSelector
          const nonInlineIndex = email.attachments
            .slice(0, originalIndex)
            .filter(att => !att.isInline && !att.is_inline).length;
          const uniqueId = getAttachmentUniqueId(attachment, nonInlineIndex);

          // Skip excluded attachments (user chose not to include in documents)
          if (excludedIds.has(uniqueId)) {
            return;
          }

          allAttachments.push({
            ...attachment,
            emailId: email.id,
            emailSubject: email.subject || '(Sans objet)',
            emailDate: email.email_time,
            senderName: email.sender_name || null,
            senderAddress: email.sender_address || null,
            attachmentIndex: originalIndex,
            emailType: email.email_type
          });
        });
      }
    });

    // Sort by email date (newest first)
    return allAttachments.sort((a, b) => {
      const dateA = new Date(a.emailDate).getTime();
      const dateB = new Date(b.emailDate).getTime();
      return dateB - dateA;
    });
  }, [emails]);
};

/**
 * Get statistics about attachments
 */
export const useAttachmentStats = (attachments: AttachmentWithContext[]) => {
  return useMemo(() => {
    const totalSize = attachments.reduce((sum, att) => sum + (att.size || 0), 0);
    const totalCount = attachments.length;

    // Group by file type
    const byType: Record<string, number> = {};
    attachments.forEach((att) => {
      const ext = att.name.split('.').pop()?.toLowerCase() || 'other';
      byType[ext] = (byType[ext] || 0) + 1;
    });

    return {
      totalCount,
      totalSize,
      byType
    };
  }, [attachments]);
};
